import { app, authentication, pages } from "@microsoft/teams-js";
import { ENV } from "./env";

export type ThemeName = "light" | "dark";
export type Frame = "personal" | "tab" | "config" | "other";

export interface TeamsInfo {
  inTeams: boolean;
  theme: ThemeName;
  /** Teams UI locale ("fr-FR"); the user's GuestPilot locale from `/me` wins once loaded */
  locale: string;
  /** Microsoft 365 group id of the Team the tab runs in (never taken from the URL, team-tab.md) */
  groupId?: string | undefined;
  frame: Frame;
  /** Teams on iOS or Android: no prices, plans, billing or upgrade prompts there (Teams Store rule for the mobile experience) */
  mobile: boolean;
  /** Inside Teams the card is entered in the browser, outside Teams (Teams Store rule, D-165); in a plain browser it is entered in the page */
  externalPayment?: boolean;
  /** Opened in a plain browser, outside Teams: the visitor sees the public side of GuestPilot (privacy, terms, help, support, Marketplace), not the app */
  publicSite?: boolean;
  /** deep-link target (`/review/:campaignId`) passed by a notification deep link */
  subPageId?: string | undefined;
}

/** Teams `default` → light; `dark` and `contrast` → dark (04 §9). */
export const toTheme = (t: string | undefined): ThemeName =>
  t === "dark" || t === "contrast" ? "dark" : "light";

/** Outside Teams (browser development) `?theme=dark` previews the dark theme. */
const previewTheme = (): ThemeName =>
  typeof location !== "undefined" && new URLSearchParams(location.search).get("theme") === "dark"
    ? "dark"
    : "light";

/** Outside Teams (browser development) `?mobile=1` previews the mobile rules. */
const previewMobile = (): boolean =>
  typeof location !== "undefined" && new URLSearchParams(location.search).get("mobile") === "1";

const MOBILE_CLIENTS = new Set(["android", "ios", "ipados"]);

/** Local development and tests open the app itself outside Teams (mock data or a development token): they keep it. */
const appOutsideTeams = () => ENV.mock || Boolean(ENV.devToken);

/**
 * Is this page probably inside Teams? Teams shows an app in a frame (or in its own desktop and mobile web views, whose user agent says Teams).
 * A top-level page of an ordinary browser is not: it does not wait for the Teams handshake and shows the public side at once.
 */
export function likelyInTeams(): boolean {
  if (typeof window === "undefined") return false;
  return window.self !== window.top || /Teams|TeamsMobile/i.test(navigator.userAgent);
}

const FALLBACK: TeamsInfo = {
  inTeams: false,
  publicSite: !appOutsideTeams(),
  mobile: previewMobile(),
  theme: previewTheme(),
  locale: typeof navigator === "undefined" ? "en" : navigator.language,
  frame: "personal",
};

/** Initialises the Teams SDK; outside Teams (browser development, tests) it resolves quickly with the fallback. */
export async function initTeams(timeoutMs = 1500): Promise<TeamsInfo> {
  try {
    await Promise.race([
      app.initialize(),
      new Promise((_r, rej) => setTimeout(() => rej(new Error("not in Teams")), timeoutMs)),
    ]);
    const ctx = await app.getContext();
    const frameContext = ctx.page.frameContext;
    const frame: Frame =
      frameContext === "settings" ? "config" : ctx.team?.groupId || ctx.channel ? "tab" : "personal";
    app.notifySuccess();
    return {
      inTeams: true,
      theme: toTheme(ctx.app.theme),
      locale: ctx.app.locale,
      groupId: ctx.team?.groupId,
      frame,
      mobile: MOBILE_CLIENTS.has(String(ctx.app.host.clientType)),
      externalPayment: true,
      subPageId: ctx.page.subPageId,
    };
  } catch {
    return FALLBACK;
  }
}

export function onThemeChange(cb: (t: ThemeName) => void): void {
  try {
    app.registerOnThemeChangeHandler((t) => cb(toTheme(t)));
  } catch {
    /* outside Teams */
  }
}

let cached: { token: string; at: number } | undefined;
let bearerOverride: string | undefined;

/** The browser payment page is not a Teams page: its single-use link is the bearer of its calls (D-165). */
export function setBearerOverride(token: string | undefined): void {
  bearerOverride = token;
}

/** Opens a page outside Teams: the system browser from Teams, a new tab elsewhere. Resolves once the request was handed over. */
export async function openExternal(url: string): Promise<void> {
  try {
    await app.openLink(url);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/** Teams SSO token for the API; cached for a few minutes (the API validates audience, issuer and expiry, BR-002). */
export async function getSsoToken(): Promise<string | undefined> {
  if (bearerOverride) return bearerOverride;
  if (ENV.devToken) return ENV.devToken;
  if (cached && Date.now() - cached.at < 4 * 60_000) return cached.token;
  try {
    const token = await authentication.getAuthToken();
    cached = { token, at: Date.now() };
    return token;
  } catch {
    return undefined;
  }
}

export function clearSsoCache(): void {
  cached = undefined;
}

/**
 * Step-up re-authentication (BR-175): an Entra popup with `prompt=login&max_age=0`; the popup page `/auth-end` returns a fresh
 * access token whose `auth_time` is now. The token is used for the one call that needs it.
 */
export async function stepUp(): Promise<string> {
  if (ENV.mock) return "mock-stepup-token";
  const url = `${window.location.origin}/auth-start?stepup=1`;
  return authentication.authenticate({ url, width: 600, height: 700 });
}

/** Copy of the Teams deep-link builders is in the API; the SPA only opens them through `pages.navigateToApp`. */
export async function openSubPage(appId: string, pageId: string, subPageId?: string): Promise<void> {
  await pages.navigateToApp({ appId, pageId, ...(subPageId ? { subPageId } : {}) });
}

export const notifyAuthSuccess = (result: string): void => authentication.notifySuccess(result);
export const notifyAuthFailure = (reason: string): void => authentication.notifyFailure(reason);

/** Opens an Entra or consent page in the Teams authentication popup; resolves when the popup page reports back (`/auth-end`). */
export async function authenticateInTeams(url: string): Promise<string> {
  if (ENV.mock) return "ok";
  // Teams only opens a popup on a page of our own domain (`validDomains`, which holds no Microsoft host): an Entra or consent URL goes
  // through `/auth-start?next=`, which redirects to it once the popup is open
  const own = new URL(url, window.location.origin).origin === window.location.origin;
  const start = own ? url : `${window.location.origin}/auth-start?next=${encodeURIComponent(url)}`;
  return authentication.authenticate({ url: start, width: 600, height: 700 });
}
